import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import MenuItem from '@mui/material/MenuItem'
import Button from '@mui/material/Button'
import Grid from '@mui/material/Grid'
import Alert from '@mui/material/Alert'
import Snackbar from '@mui/material/Snackbar'
import { useAuth } from '../../shared/context/AuthContext'
import { useCreateAutomaticDiscountCouponMutation } from './hooks/useDiscountCouponQuery'
import { VALID_FOR_OPTIONS } from './discountCouponConstants'
import { buildTodayDateInputValue, validateDueDate } from './discountCouponDateUtils'

/**
 * Port of `app/discountCoupon/automaticDiscountCouponsAdd.html` +
 * `app/discountCoupon/automaticDiscountCouponsAddController.js` (AngularJS
 * `automaticDiscountCouponsAddController`, 235 lines) — the "Adicionar
 * Cupons Automáticos" screen, reached at
 * `/workspace/discount-coupons/add/automatic` (legacy state
 * `workspace.automaticDiscountCouponsAdd`, see `AppRoutes.jsx`). Task 033
 * scope (one of the 3 specialized "add coupon" variants).
 *
 * Fields, 1:1 with the legacy template:
 * - `category` (select, defaults `COMERCIAL`: Comercial/Upgrade) — "Desconto
 *   para". Drives the extra "Valor mínimo de upgrade" column below.
 * - `fromDate` (masked `dd/mm/yyyy` text in legacy, defaulted to today via
 *   `buildStartDate()`, required) — "Data de início". Ported as native MUI
 *   `TextField type="date"`, same precedent as `ActionAuditPage.jsx`
 *   (`YYYY-MM-DD` <-> `dd/mm/yyyy` conversion at the request-building
 *   boundary, not in state).
 * - `dueDate` (masked `dd/mm/yyyy` text, required, no default) — "Aplicação
 *   expira em". Same `type="date"` treatment.
 * - `validFor` (select, defaults `ONE_MONTH`, same 6 options as
 *   `AmbassadorDiscountCouponAddPage.jsx`) — "Válido a partir da aplicação
 *   por".
 * - One discount-value number input per plan in `$scope.plans` (22 plans —
 *   more than the ambassador variant's 8: adds VIP_3K/5K/10K/20K/40K/45K/50K
 *   x Mensal/Anual), plus a second "Valor mínimo de upgrade" number input
 *   per plan shown only `ng-show="discountCoupon.category === 'UPGRADE'"`.
 * - `code` (text, required) — "Prefixo do cupom".
 * - `vendor` (text, required, defaulted to the logged-in user) — "Quem criou
 *   o cupom".
 * - `observation` (multiline, optional) — "Observações".
 *
 * Same controller-construction-time-vs-`ng-init` ordering note as
 * `AmbassadorDiscountCouponAddPage.jsx`'s doc-comment applies here too
 * (`$scope.plans.forEach` guarded by `if (!!$scope.discountCoupon)`, always
 * `false` at that point) — each discount/minimumUpgrade field starts empty,
 * not `0`.
 *
 * `$scope.save(discountCoupon)`:
 * - Validates `dueDate` (only if non-empty — the field is marked `required`
 *   in the template but the JS re-validates leniently): splits on `/`,
 *   bails silently (`return`, no toast) if it doesn't have exactly 3 parts,
 *   else rejects with `Materialize.toast('A expiração deve ser uma data
 *   futura', 6000)` if the parsed date is `<=` now. Ported as
 *   `validateDueDate` below, called before building the payload.
 * - `buildPlansDiscountsData`: for every `discountValues[key]` entry with a
 *   truthy `.discount`, splits `key` on `-` into `plan`/`recurrencyType`,
 *   emits `{ plan, recurrencyType, value: discount }`, and — only when
 *   `category === 'UPGRADE'` and `.minimumUpgrade` is truthy — adds
 *   `minimumValueUpgrade`.
 * - `buildCampaignData`: wraps a single campaign object (`{
 *   discountCouponCodePrefix: code, profileType:
 *   DISCOUNT_COUPON_CATEGORY_MAP[category], validFor, discounts }`) in an
 *   array — `DISCOUNT_COUPON_CATEGORY_MAP` maps `COMERCIAL` ->
 *   `NEW_CLIENTS`, `UPGRADE` -> `UPGRADE`.
 * - `buildAutomaticCouponsPayload`: `{ vendor, couponClass:
 *   'CL_REGULAR_TEMPORARY', startDate: fromDate, dueDateToApply: dueDate ??
 *   '', observation: observation ?? '', campaigns: [campaign] }`.
 * - `discountCoupon.userViewerLogin = AuthService.getUserLogin()` set before
 *   building the log payload, then
 *   `DiscountCouponsService.saveAutomaticCoupons(couponsPayload)` +
 *   `saveStatusLog(logPayload)` fire in that order (not chained) — ported as
 *   `useCreateAutomaticDiscountCouponMutation`.
 * - Success: toast + `$state.go('workspace.discountCoupons')`. Error: resets
 *   `discountCoupon.fromDate` to its pre-save value (a no-op here since this
 *   port never mutates `fromDate` as part of the save itself — the legacy
 *   reset exists because `setDiscountCupomLogInfo`/`buildCampaignData` don't
 *   touch `fromDate`, so nothing to restore either) + toast with
 *   `error.data?.errorMessage`.
 *
 * Cancel/submit-disabled behavior: same pattern as the ambassador variant —
 * `code`/`fromDate`/`dueDate`/`vendor` all `required` in the legacy
 * template, so the submit button is disabled until all four are filled in.
 */

const CATEGORY_OPTIONS = [
  { value: 'COMERCIAL', label: 'Comercial' },
  { value: 'UPGRADE', label: 'Upgrade' },
]

/**
 * Port of `$scope.plans` in `automaticDiscountCouponsAddController.js`
 */
const PLANS = [
  { key: 'UP-MONTHLY', tag: 'Up Mensal' },
  { key: 'UP-YEARLY', tag: 'Up Anual' },
  { key: 'UP-YEARLY_SPLITED', tag: 'Up Anual Parcelado' },
  { key: 'SMART-MONTHLY', tag: 'Smart Mensal' },
  { key: 'SMART-YEARLY', tag: 'Smart Anual' },
  { key: 'SMART-YEARLY_SPLITED', tag: 'Smart Anual Parcelado' },
  { key: 'COMPANY-MONTHLY', tag: 'Company Mensal' },
  { key: 'COMPANY-YEARLY', tag: 'Company Anual' },
  { key: 'COMPANY-YEARLY_SPLITED', tag: 'Company Anual Parcelado' },
  { key: 'VIP-MONTHLY', tag: 'Vip Mensal' },
  { key: 'VIP-YEARLY', tag: 'Vip Anual' },
  { key: 'VIP-YEARLY_SPLITED', tag: 'Vip Anual Parcelado' },
  { key: 'VIP_3K-MONTHLY', tag: 'Vip 3.000 Mensal' },
  { key: 'VIP_3K-YEARLY', tag: 'Vip 3.000 Anual' },
  { key: 'VIP_3K-YEARLY_SPLITED', tag: 'Vip 3.000 Anual Parcelado' },
  { key: 'VIP_5K-MONTHLY', tag: 'Vip 5.000 Mensal' },
  { key: 'VIP_5K-YEARLY', tag: 'Vip 5.000 Anual' },
  { key: 'VIP_5K-YEARLY_SPLITED', tag: 'Vip 5.000 Anual Parcelado' },
  { key: 'VIP_10K-MONTHLY', tag: 'Vip 10.000 Mensal' },
  { key: 'VIP_10K-YEARLY', tag: 'Vip 10.000 Anual' },
  { key: 'VIP_10K-YEARLY_SPLITED', tag: 'Vip 10.000 Anual Parcelado' },
  { key: 'VIP_20K-MONTHLY', tag: 'Vip 20.000 Mensal' },
  { key: 'VIP_20K-YEARLY', tag: 'Vip 20.000 Anual' },
  { key: 'VIP_20K-YEARLY_SPLITED', tag: 'Vip 20.000 Anual Parcelado' },
  { key: 'VIP_40K-MONTHLY', tag: 'Vip 40.000 Mensal' },
  { key: 'VIP_40K-YEARLY', tag: 'Vip 40.000 Anual' },
  { key: 'VIP_40K-YEARLY_SPLITED', tag: 'Vip 40.000 Anual Parcelado' },
  { key: 'VIP_45K-MONTHLY', tag: 'Vip 45.000 Mensal' },
  { key: 'VIP_45K-YEARLY', tag: 'Vip 45.000 Anual' },
  { key: 'VIP_45K-YEARLY_SPLITED', tag: 'Vip 45.000 Anual Parcelado' },
  { key: 'VIP_50K-MONTHLY', tag: 'Vip 50.000 Mensal' },
  { key: 'VIP_50K-YEARLY', tag: 'Vip 50.000 Anual' },
  { key: 'VIP_50K-YEARLY_SPLITED', tag: 'Vip 50.000 Anual Parcelado' },
]

/** Port of `DISCOUNT_COUPON_CATEGORY_MAP`. */
const DISCOUNT_COUPON_CATEGORY_MAP = {
  COMERCIAL: 'NEW_CLIENTS',
  UPGRADE: 'UPGRADE',
}

/** Port of `buildPlansDiscountsData({ discountValues, category })`. */
function buildPlansDiscountsData(discountValues, category) {
  const allDiscounts = []

  Object.entries(discountValues).forEach(([key, entry]) => {
    if (!entry?.discount) return

    const [plan, recurrencyType] = key.split('-')
    const discount = { plan, recurrencyType, value: entry.discount }

    if (category === 'UPGRADE' && entry.minimumUpgrade) {
      discount.minimumValueUpgrade = entry.minimumUpgrade
    }

    allDiscounts.push(discount)
  })

  return allDiscounts
}

/** Port of `buildCampaignData(discountCoupon)`. */
function buildCampaignData(form) {
  return [
    {
      discountCouponCodePrefix: form.code,
      profileType: DISCOUNT_COUPON_CATEGORY_MAP[form.category],
      validFor: form.validFor,
      discounts: buildPlansDiscountsData(form.discountValues, form.category),
    },
  ]
}

/** Port of `buildAutomaticCouponsPayload(discountCoupon)`. */
function buildAutomaticCouponsPayload(form) {
  return {
    vendor: form.vendor,
    couponClass: 'CL_REGULAR_TEMPORARY',
    startDate: form.fromDate,
    dueDateToApply: form.dueDate ?? '',
    observation: form.observation ?? '',
    campaigns: buildCampaignData(form),
  }
}

function AutomaticDiscountCouponAddPage() {
  const navigate = useNavigate()
  const { getUserName } = useAuth()
  const createMutation = useCreateAutomaticDiscountCouponMutation()

  const [category, setCategory] = useState('COMERCIAL')
  const [fromDate, setFromDate] = useState(buildTodayDateInputValue())
  const [dueDate, setDueDate] = useState('')
  const [validFor, setValidFor] = useState('ONE_MONTH')
  const [discountValues, setDiscountValues] = useState({})
  const [code, setCode] = useState('')
  const [vendor, setVendor] = useState(getUserName() || '')
  const [observation, setObservation] = useState('')
  const [snackbar, setSnackbar] = useState({ open: false, message: '' })

  const isUpgrade = category === 'UPGRADE'

  function notify(message) {
    setSnackbar({ open: true, message })
  }

  function handleSnackbarClose() {
    setSnackbar((current) => ({ ...current, open: false }))
  }

  function handleDiscountChange(key, field, value) {
    setDiscountValues((current) => ({
      ...current,
      [key]: { ...current[key], [field]: value === '' ? undefined : Number(value) },
    }))
  }

  const isValid = code.trim() !== '' && fromDate !== '' && dueDate !== '' && vendor.trim() !== ''

  /** Port of `$scope.save(discountCoupon)`. */
  function handleSave() {
    const dueDateError = validateDueDate(dueDate)
    if (dueDateError === 'invalid-format') return
    if (dueDateError === 'not-future') {
      notify('A expiração deve ser uma data futura')
      return
    }

    const form = {
      category,
      fromDate,
      dueDate,
      validFor,
      discountValues,
      code,
      vendor,
      observation,
    }
    const couponsPayload = buildAutomaticCouponsPayload(form)

    createMutation.mutate(couponsPayload, {
      onSuccess: () => {
        notify('Cupons de desconto criados com sucesso!')
        navigate('/workspace/discount-coupons')
      },
      onError: (error) =>
        notify(
          `Erro ao cadastrar os cupons de desconto: ${error?.response?.data?.errorMessage || error?.message || ''}`,
        ),
    })
  }

  return (
    <Box>
      <Typography variant="h5" component="h2" gutterBottom>
        Adicionar Cupons Automáticos
      </Typography>

      <Paper sx={{ p: 3 }} variant="outlined">
        <Grid container spacing={2}>
          <Grid size={12}>
            <TextField
              id="category"
              select
              label="Desconto para*"
              value={category}
              onChange={(event) => setCategory(event.target.value)}
              fullWidth
            >
              {CATEGORY_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          <Grid size={6}>
            <TextField
              id="fromDate"
              type="date"
              label="Data de início*"
              value={fromDate}
              onChange={(event) => setFromDate(event.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              fullWidth
              required
            />
          </Grid>
          <Grid size={6}>
            <TextField
              id="dueDate"
              type="date"
              label="Aplicação expira em*"
              value={dueDate}
              onChange={(event) => setDueDate(event.target.value)}
              slotProps={{ inputLabel: { shrink: true } }}
              fullWidth
              required
            />
          </Grid>

          <Grid size={12}>
            <TextField
              id="validFor"
              select
              label="Válido a partir da aplicação por*"
              value={validFor}
              onChange={(event) => setValidFor(event.target.value)}
              fullWidth
            >
              {VALID_FOR_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>

          {PLANS.map((plan) => (
            <Grid key={plan.key} size={12} container spacing={2} sx={{ alignItems: 'center' }}>
              <Grid size={isUpgrade ? 4 : 4}>
                <Typography variant="body2">
                  <strong>{plan.tag}</strong>
                </Typography>
              </Grid>
              <Grid size={isUpgrade ? 4 : 8}>
                <TextField
                  id={plan.key}
                  type="number"
                  label="Valor de desconto"
                  value={discountValues[plan.key]?.discount ?? ''}
                  onChange={(event) =>
                    handleDiscountChange(plan.key, 'discount', event.target.value)
                  }
                  inputProps={{ min: 1, step: 'any' }}
                  fullWidth
                />
              </Grid>
              {isUpgrade && (
                <Grid size={4}>
                  <TextField
                    id={`${plan.key}_MIN_UPGRADE`}
                    type="number"
                    label="Valor mínimo de upgrade"
                    value={discountValues[plan.key]?.minimumUpgrade ?? ''}
                    onChange={(event) =>
                      handleDiscountChange(plan.key, 'minimumUpgrade', event.target.value)
                    }
                    inputProps={{ min: 1, step: 'any' }}
                    fullWidth
                  />
                </Grid>
              )}
            </Grid>
          ))}

          <Grid size={12}>
            <TextField
              id="code"
              label="Prefixo do cupom*"
              value={code}
              onChange={(event) => setCode(event.target.value)}
              fullWidth
              required
            />
          </Grid>
          <Grid size={12}>
            <TextField
              id="vendor"
              label="Quem criou o cupom*"
              value={vendor}
              onChange={(event) => setVendor(event.target.value)}
              fullWidth
              required
            />
          </Grid>
          <Grid size={12}>
            <TextField
              id="observation"
              label="Observações"
              value={observation}
              onChange={(event) => setObservation(event.target.value)}
              fullWidth
              multiline
              minRows={3}
            />
          </Grid>

          <Grid size={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
            <Button onClick={() => navigate('/workspace/discount-coupons')}>Cancelar</Button>
            <Button
              variant="contained"
              onClick={handleSave}
              disabled={!isValid || createMutation.isPending}
            >
              Salvar
            </Button>
          </Grid>
        </Grid>
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity="info"
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  )
}

export default AutomaticDiscountCouponAddPage
